import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { createBrowserHistory } from 'history';
import { stringHasValue } from '@/utils/stringUtil';

type TelemetryEnvelope = {
  tags?: Record<string, string>;
  baseType?: string;
  baseData?: {
    name?: string;
    target?: string;
    data?: string;
  };
};

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
const clickPluginInstance = new ClickAnalyticsPlugin();
const clickPluginConfig = {
  autoCapture: true,
  dataTags: { useDefaultContentNameOrId: true },
};

const appInsightsConnectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string;
const dependencyIgnorePatternsString = import.meta.env
  .VITE_APPINSIGHTS_DEPENDENCY_IGNORE_PATTERNS as string;
const dependencyIgnorePatterns: string[] = !stringHasValue(dependencyIgnorePatternsString)
  ? []
  : dependencyIgnorePatternsString
      .split(',')
      .map((pattern) => pattern.trim().toLowerCase())
      .filter((pattern) => pattern.length > 0);

function shouldIgnoreDependency(envelope: TelemetryEnvelope): boolean {
  if (envelope.baseType !== 'RemoteDependencyData') {
    return false;
  }

  const baseData = envelope.baseData ?? {};
  const value = `${baseData.name ?? ''} ${baseData.target ?? ''} ${baseData.data ?? ''}`
    .toLowerCase()
    .trim();

  if (value.length === 0) {
    return false;
  }

  return dependencyIgnorePatterns.some((pattern) => value.includes(pattern));
}

const appInsights = new ApplicationInsights({
  config: {
    connectionString: appInsightsConnectionString,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin, clickPluginInstance],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
      [clickPluginInstance.identifier]: clickPluginConfig,
    },
  },
});

const appInsightsRoleName = (import.meta.env.VITE_APPINSIGHTS_ROLE_NAME ??
  'RPA-Frontend') as string;
const hasTelemetryConnectionString = stringHasValue(appInsightsConnectionString);

if (hasTelemetryConnectionString) {
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((envelope) => {
    const telemetryEnvelope = envelope as TelemetryEnvelope;

    telemetryEnvelope.tags = telemetryEnvelope.tags != null ? telemetryEnvelope.tags : {};
    telemetryEnvelope.tags['ai.cloud.role'] = appInsightsRoleName;
    telemetryEnvelope.tags['ai.cloud.roleInstance'] = window.location.hostname;

    // Filter noisy dependencies like health checks and static infrastructure calls.
    if (shouldIgnoreDependency(telemetryEnvelope)) {
      return false;
    }

    return true;
  });
} else {
  // Allow local/dev environments to run without telemetry configuration.
  console.warn('Application Insights disabled: VITE_APPINSIGHTS_CONNECTION_STRING is not set.');
}

export { reactPlugin, appInsights };
